// ============================================================================
// PARTIE 1/6 : IMPORTS ET ÉTATS
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { aUnePhoto, PARAM_PHOTO, VALEUR_SANS_PHOTO } from '../utils/photos';
import { useParametres } from '../contexts/ParametresContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { Card, CardDescription, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import { Toast } from '../components/ui/Toast';
import { LongToast, shouldUseLongToast } from '../components/ui/LongToast';
import {
  Search, Download, FileText, Eye,
  Plus, Trash2, ChevronDown, Users, BookOpen,
  Percent, UserCheck, UserX, CreditCard,
  Folder, School, UserPlus,
  Loader2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import DetailModal from '../components/DetailModal';
import { parseISO, format } from 'date-fns';
import ProtectedComponent from '../components/ProtectedComponent';
import ConfirmDialog from '../components/ConfirmDialog';


// Les trois manières dont un élève entre dans l'effectif de l'année. Les
// variantes 'reinscription' et 'réinscription' sont celles que le serveur
// reconnaît déjà ailleurs (familles, comptabilité) : on lit la même règle.
// Tout ce qui n'est ni une réinscription ni un transfert est une nouvelle
// inscription, comme pour le calcul des frais de début d'année.
const TYPES_REINSCRIPTION = new Set(['renewal', 'reinscription', 'réinscription']);
const typeInscriptionEleve = (eleve) => {
  const type = String(eleve?.type_inscription || '').trim().toLowerCase();
  if (TYPES_REINSCRIPTION.has(type)) return 'reinscription';
  if (type === 'transfert') return 'transfert';
  return 'nouveau';
};

const StudentsList = () => {
  // Deux modules gouvernent ce que cet écran montre au-delà de l'état civil :
  // les familles nombreuses et la comptabilité. Sans eux, la colonne, la carte
  // et la colonne d'export correspondantes n'auraient que du vide à afficher.
  const { estActive } = useFeatures();
  const famillesOuvertes = estActive('module.familles');
  const comptabiliteOuverte = estActive('module.comptabilite');

  const navigate = useNavigate();

  // 🔥 Récupérer les sections depuis le Provider
  const {
    parametres,
    getSectionColors,
  } = useParametres();

  const [sectionsStables, setSectionsStables] = useState([]);

  // États principaux
  const [currentUser, setCurrentUser] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  // { annee_id, annee_nom, effectif, reinscrits_partis } de l'année
  // précédente, ou null
  // (première année de l'école, ou lecture en échec).
  const [anneePrecedente, setAnneePrecedente] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [eleves, setEleves] = useState([]);
  // Les demandes de radiation en attente, indexées par identifiant d'élève.
  // C'est ce qui permet d'afficher « Radiation demandée » sur la ligne : sans ce
  // repère, rien ne distingue un élève dont la radiation est en cours de
  // validation d'un élève que personne n'a touché.
  const [radiationsEnAttente, setRadiationsEnAttente] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  const [classes, setClasses] = useState([]);


  const [ecole, setEcole] = useState(null);

  // 🔥 NOUVEAU : États pour gérer l'expansion des sections et classes
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  // Filtre « Sans photo » : '' (tous les élèves) ou VALEUR_SANS_PHOTO. Il
  // s'applique à la racine, comme la recherche globale, et se combine avec les
  // filtres locaux des sections et des classes.
  const [filtrePhoto, setFiltrePhoto] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [sectionSearchTerms, setSectionSearchTerms] = useState({});
  const [classSearchTerms, setClassSearchTerms] = useState({});
  const [sectionFilters, setSectionFilters] = useState({});
  const [classFilters, setClassFilters] = useState({});
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [filterSearchTerms, setFilterSearchTerms] = useState({});
  const [showPdfExportModal, setShowPdfExportModal] = useState(false);
  const [pdfExportSectionId, setPdfExportSectionId] = useState('');
  const [pdfExportClasseIds, setPdfExportClasseIds] = useState([]);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États modaux et édition
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedEleve, setEditedEleve] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // États radiation
  const [eleveToRadiate, setEleveToRadiate] = useState(null);
  const [showConfirmRadiation, setShowConfirmRadiation] = useState(false);
  const [radiationPreview, setRadiationPreview] = useState(null);
  const [isLoadingRadiationPreview, setIsLoadingRadiationPreview] = useState(false);
  const [isRadiating, setIsRadiating] = useState(false);

  // États toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  // Les options du message courant — `long`, `title`, `persistent`. C'est ce
  // qui permet à `shouldUseLongToast` de trancher autrement que sur la seule
  // longueur du texte.
  const [toastOptions, setToastOptions] = useState({});




  // ========================================================================
  // SUITE DANS LA PARTIE 2...
  // ========================================================================

  // ============================================================================
  // PARTIE 2/6 : FONCTIONS UTILITAIRES
  // ============================================================================

  // 🔥 Synchroniser les sections du Provider
  useEffect(() => {
    const nouvelles = (parametres.sections || []).filter(s => s.active === true);
    console.log('📚 [STUDENTS] Sections chargées:', nouvelles.map(s => ({ id: s.id, nom: s.nom })));
    setSectionsStables(nouvelles);
  }, [parametres.sections]);


  // À ajouter dans useEffect au chargement
  useEffect(() => {
    const cleanOldViews = () => {
      const viewedData = localStorage.getItem('viewedClassesWithDate');
      if (!viewedData) return;

      const parsed = JSON.parse(viewedData);
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;

      const filtered = parsed.filter(item => (now - item.date) < sevenDays);
      localStorage.setItem('viewedClassesWithDate', JSON.stringify(filtered));

      setViewedClasses(new Set(filtered.map(item => item.classeId)));
    };

    cleanOldViews();
  }, []);


  // ============================================================================
  // 🔥 NOUVEAU : Stabilisation des props pour éviter les re-renders inutiles
  // ============================================================================
  const stableSelectedEleve = useMemo(() => {
    return selectedEleve ? {
      ...selectedEleve,
      // Garantir que les propriétés essentielles existent
      id: selectedEleve.id,
      nom: selectedEleve.nom,
      prenom: selectedEleve.prenom,
      classe_id: selectedEleve.classe_id,
      classe_nom: selectedEleve.classe_nom
    } : null;
  }, [selectedEleve?.id, selectedEleve?.classe_id]); // ✅ Ne change que si l'ID ou la classe change

  const stableActiveYear = useMemo(() => {
    return activeYear ? {
      id: activeYear.id,
      nom: activeYear.nom
    } : null;
  }, [activeYear?.id]); // ✅ Ne change que si l'ID change

  const stableCurrentUser = useMemo(() => {
    return currentUser ? {
      id: currentUser.id,
      nom: currentUser.nom,
      prenom: currentUser.prenom
    } : null;
  }, [currentUser?.id]); // ✅ Ne change que si l'ID change


  useEffect(() => {
    let cancelled = false;

    const loadRadiationPreview = async () => {
      if (!showConfirmRadiation || !eleveToRadiate?.id || !activeYear?.id) {
        setRadiationPreview(null);
        return;
      }

      const echec = comptabiliteOuverte
        ? "Impossible de charger l'analyse comptable de radiation."
        : "Impossible de préparer la radiation.";

      try {
        setIsLoadingRadiationPreview(true);
        const result = await api.eleves.previewRadiation(eleveToRadiate.id, {
          date_radiation: new Date().toISOString().split("T")[0],
          annee_scolaire_id: activeYear.id,
        });

        if (!cancelled) {
          setRadiationPreview(
            result?.success === false
              ? { error: result.message || echec }
              : (result?.data || result)
          );
        }
      } catch (error) {
        if (!cancelled) {
          setRadiationPreview({ error: error.message || echec });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRadiationPreview(false);
        }
      }
    };

    loadRadiationPreview();

    return () => {
      cancelled = true;
    };
  }, [showConfirmRadiation, eleveToRadiate?.id, activeYear?.id, comptabiliteOuverte]);

  // Fonctions utilitaires
  const formatMontant = (montant) => {
    if (montant === null || montant === undefined) return "0 FCFA";
    return Math.round(Number(montant) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " FCFA";
  };

  const renderRadiationPreview = () => {
    if (isLoadingRadiationPreview) {
      return (
        <div className="rounded-xl border border-[#246990]/20 bg-[#246990]/5 p-4 text-sm text-[#246990]">
          {comptabiliteOuverte ? 'Analyse comptable en cours...' : 'Préparation de la radiation...'}
        </div>
      );
    }

    if (radiationPreview?.error) {
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {radiationPreview.error}
        </div>
      );
    }

    if (!radiationPreview) return null;

    const resume = radiationPreview.resume || {};
    const moisEffet = radiationPreview.mois_effet;
    const famille = radiationPreview.famille;
    const moisConserves = radiationPreview.mois_conserves || [];
    const moisANeutraliser = radiationPreview.mois_a_neutraliser || [];
    const moisPartiels = radiationPreview.mois_partiels || [];
    const visibleConserves = moisConserves.slice(0, 4);
    const visibleNeutralises = moisANeutraliser.slice(0, 4);

    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-[#246990]/20 bg-[#246990]/5 p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#246990]">
                {comptabiliteOuverte ? 'Lecture comptable avant radiation' : 'Ce qui va se passer'}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-gray-600">
                {comptabiliteOuverte
                  ? "Les paiements déjà encaissés restent dans l'historique. Les mois non commencés seront exclus de l'attendu après radiation."
                  : "L'élève sera retiré des effectifs. Son dossier reste consultable depuis la liste des élèves radiés."}
              </p>
            </div>
            {comptabiliteOuverte && moisEffet && (
              <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                Effet proposé : {moisEffet.nom}
              </span>
            )}
          </div>

          {/* Quatre compteurs financiers : sans comptabilite ils afficheraient
              tous zero, et « a neutraliser : 54 000 FCFA » se lirait comme une
              dette dans une ecole qui n encaisse rien. */}
          {comptabiliteOuverte && (
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div className="rounded-lg border border-white/80 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">Encaissé conservé</p>
              <p className="mt-1 text-lg font-bold text-emerald-700">{formatMontant(resume.total_encaisse_conserve)}</p>
            </div>
            <div className="rounded-lg border border-white/80 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">Payés / partiels</p>
              <p className="mt-1 text-lg font-bold text-[#246990]">{resume.nb_mois_conserves || 0}</p>
            </div>
            <div className="rounded-lg border border-white/80 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">À neutraliser</p>
              <p className="mt-1 text-lg font-bold text-red-700">{formatMontant(resume.total_attendu_a_neutraliser)}</p>
            </div>
            <div className="rounded-lg border border-white/80 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">Reste partiel</p>
              <p className="mt-1 text-lg font-bold text-amber-700">{formatMontant(resume.total_reste_partiel_conserve)}</p>
            </div>
          </div>
          )}
        </div>

        {famillesOuvertes && famille && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-amber-900">Famille nombreuse détectée : {famille.nom}</p>
                <p className="mt-1 text-xs text-amber-800">
                  Membres actifs : {famille.membres_actifs_avant} avant radiation, {famille.membres_actifs_apres} après radiation.
                </p>
              </div>
              {famille.accord_financier_actif && (
                <span className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-800">
                  Accord financier actif #{famille.accord_financier_actif.id}
                </span>
              )}
            </div>
            <p className="mt-2 text-xs leading-relaxed text-amber-800">{famille.message}</p>
          </div>
        )}

        {/* Sans comptabilite, aucun mois n a pu etre encaisse : ce bilan
            n afficherait que des zeros et un avertissement sur des impayes qui
            n existent pas. La radiation elle-meme reste possible. */}
        {comptabiliteOuverte && (visibleConserves.length > 0 || visibleNeutralises.length > 0 || moisPartiels.length > 0) && (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <p className="mb-2 text-xs font-semibold uppercase text-gray-500">Mois encaissés ou partiels</p>
              {visibleConserves.length === 0 ? (
                <p className="text-xs text-gray-500">Aucun mois encaissé ou partiel détecté.</p>
              ) : (
                <div className="space-y-1.5">
                  {visibleConserves.map((mois) => (
                    <div key={`conserve-${mois.id}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-medium text-gray-700">{mois.mois_nom}</span>
                      <span className="text-gray-500">{formatMontant(mois.montant_paye)} encaissé</span>
                    </div>
                  ))}
                  {moisConserves.length > visibleConserves.length && (
                    <p className="text-xs text-gray-400">+ {moisConserves.length - visibleConserves.length} autre(s) mois encaissé(s) ou partiel(s)</p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-red-100 bg-red-50 p-3">
              <p className="mb-2 text-xs font-semibold uppercase text-red-700">Mois futurs à neutraliser</p>
              {visibleNeutralises.length === 0 ? (
                <p className="text-xs text-red-700">Aucun mois totalement non payé avec montant attendu détecté.</p>
              ) : (
                <div className="space-y-1.5">
                  {visibleNeutralises.map((mois) => (
                    <div key={`neutralise-${mois.id}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-medium text-red-800">{mois.mois_nom}</span>
                      <span className="text-red-700">{formatMontant(mois.montant_attendu)}</span>
                    </div>
                  ))}
                  {moisANeutraliser.length > visibleNeutralises.length && (
                    <p className="text-xs text-red-500">+ {moisANeutraliser.length - visibleNeutralises.length} autre(s) mois à traiter</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {comptabiliteOuverte && moisPartiels.length > 0 && (
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800">
            {moisPartiels.length} mois partiel(s) détecté(s). Ils restent conservés : la radiation ne solde pas automatiquement les restes.
          </div>
        )}
      </div>
    );
  };

  const normalizeText = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const eleveHasFamily = (eleve) => Boolean(eleve.est_en_famille || eleve.famille_nom);
  const eleveHasReduction = (eleve) => Boolean(eleve.a_reduction);

  const getReductionLabel = (eleve) => {
    if (eleve.gratuit_mensuel) return 'Gratuité';

    const labels = {
      famille: 'Famille',
      personnalisee: 'Personnalisée',
      exceptionnelle: 'Exceptionnelle',
      mixte: 'Mixte',
      aucune: 'Aucune'
    };

    return labels[eleve.type_reduction] || 'Aucune';
  };

  const getReductionBadgeClass = (eleve) => {
    if (eleve.gratuit_mensuel) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (eleve.type_reduction === 'personnalisee') return 'bg-violet-100 text-violet-800 border-violet-200';
    if (eleve.type_reduction === 'famille') return 'bg-[#246990]/10 text-[#246990] border-[#246990]/20';
    if (eleve.type_reduction === 'exceptionnelle') return 'bg-amber-100 text-amber-800 border-amber-200';
    if (eleve.type_reduction === 'mixte') return 'bg-teal-100 text-teal-800 border-teal-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const getReductionAmountLabel = (eleve) => {
    if (!eleveHasReduction(eleve)) return 'Tarif normal';
    const monthlyReduction = Number(eleve.reduction_mensuel_totale || 0);
    const inscriptionReduction = Number(eleve.reduction_inscription_totale || 0);

    if (monthlyReduction > 0) return `-${formatMontant(monthlyReduction)}/mois`;
    if (inscriptionReduction > 0) return `-${formatMontant(inscriptionReduction)} inscription`;
    return 'Réduction active';
  };

  const matchesEleveSearch = (eleve, term) => {
    const normalizedTerm = normalizeText(term);
    if (!normalizedTerm) return true;

    return [
      eleve.nom,
      eleve.prenom,
      eleve.matricule,
      eleve.classe_nom,
      eleve.famille_nom,
      eleve.parent?.nom_prenom
    ].some(value => normalizeText(value).includes(normalizedTerm));
  };

  const familleFilterOptions = [
    { id: '', label: 'Toutes les familles' },
    { id: 'avec_famille', label: 'Avec famille' },
    { id: 'sans_famille', label: 'Sans famille' },
  ];

  const reductionFilterOptions = [
    { id: '', label: 'Toutes les réductions' },
    { id: 'avec_reduction', label: 'Avec réduction' },
    { id: 'sans_reduction', label: 'Sans réduction' },
    { id: 'famille', label: 'Réduction famille' },
    { id: 'personnalisee', label: 'Personnalisée' },
    { id: 'exceptionnelle', label: 'Exceptionnelle' },
    { id: 'gratuite', label: 'Gratuité' },
  ];

  const matchesFamilleFilter = (eleve, value) => {
    if (!value) return true;
    if (value === 'avec_famille') return eleveHasFamily(eleve);
    if (value === 'sans_famille') return !eleveHasFamily(eleve);
    return true;
  };

  const matchesReductionFilter = (eleve, value) => {
    if (!value) return true;
    if (value === 'avec_reduction') return eleveHasReduction(eleve);
    if (value === 'sans_reduction') return !eleveHasReduction(eleve);
    if (value === 'famille') return Boolean(eleve.reduction_famille_active);
    if (value === 'personnalisee') return Boolean(eleve.reduction_personnalisee_active);
    if (value === 'exceptionnelle') return Boolean(eleve.reduction_exceptionnelle_active);
    if (value === 'gratuite') return Boolean(eleve.gratuit_mensuel);
    return true;
  };

  const applyLocalEleveFilters = (elevesList, localFilters = {}) => {
    const { search = '', famille = '', reduction = '' } = localFilters;

    return elevesList.filter(eleve =>
      matchesEleveSearch(eleve, search) &&
      matchesFamilleFilter(eleve, famille) &&
      matchesReductionFilter(eleve, reduction)
    );
  };

  const updateSectionFilter = (sectionId, key, value) => {
    setSectionFilters(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [key]: value
      }
    }));
  };

  const updateClassFilter = (classeId, key, value) => {
    setClassFilters(prev => ({
      ...prev,
      [classeId]: {
        ...(prev[classeId] || {}),
        [key]: value
      }
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // 🔥 NOUVEAU : Organiser les élèves par section et par classe
  const organizeElevesBySection = () => {
    const organized = {};

    sectionsStables.forEach(section => {
      organized[section.id] = {
        section: section,
        classes: {}
      };
    });

    // Grouper les élèves par section et classe
    filteredEleves.forEach(eleve => {
      const classe = classes.find(c => c.id === eleve.classe_id);
      if (!classe) return;

      const sectionId = classe.section_id;
      if (!organized[sectionId]) return;

      if (!organized[sectionId].classes[classe.id]) {
        organized[sectionId].classes[classe.id] = {
          classe: classe,
          eleves: []
        };
      }

      organized[sectionId].classes[classe.id].eleves.push(eleve);
    });

    return organized;
  };

  // 🔥 NOUVEAU : Calculer les statistiques par section
  const calculateSectionStats = (sectionId) => {
    const sectionData = elevesOrganises[sectionId];
    if (!sectionData) return { totalEleves: 0, totalClasses: 0 };

    const classesArray = Object.values(sectionData.classes);
    const totalEleves = classesArray.reduce((sum, c) => sum + c.eleves.length, 0);

    return {
      totalEleves,
      totalClasses: classesArray.length
    };
  };

  // Liste de base de l'année active. Les stats restent calculees sur cette base complete.
  const elevesAnnee = activeYear
    ? eleves.filter(eleve => Number(eleve.annee_scolaire_id) === Number(activeYear.id))
    : eleves;

  const hasGlobalSearch = normalizeText(globalSearchTerm).length > 0;
  const filtreSansPhoto = filtrePhoto === VALEUR_SANS_PHOTO;

  // Recherche globale : retrouve un eleve sans connaitre sa section ni sa classe.
  // Le filtre « Sans photo » s'applique au même niveau : les pastilles des
  // sections et des classes comptent alors les manques. La règle « a une
  // photo » est celle de utils/photos (un chemin fait d'espaces n'en est pas
  // une), partagée avec le serveur et le panneau des alertes.
  const filteredEleves = (hasGlobalSearch || filtreSansPhoto)
    ? elevesAnnee.filter(eleve =>
      (!hasGlobalSearch || matchesEleveSearch(eleve, globalSearchTerm)) &&
      (!filtreSansPhoto || !aUnePhoto(eleve))
    )
    : elevesAnnee;

  // Appliquer l'organisation
  const elevesOrganises = organizeElevesBySection();

  // Trier les élèves
  const sortedEleves = [...filteredEleves].sort((a, b) => {
    const aValue = a[sortConfig.key]?.toLowerCase() || '';
    const bValue = b[sortConfig.key]?.toLowerCase() || '';

    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue);
    }
    return bValue.localeCompare(aValue);
  });

  const listStats = {
    total: elevesAnnee.length,
    enFamille: elevesAnnee.filter(eleveHasFamily).length,
    avecReduction: elevesAnnee.filter(eleveHasReduction).length,
    sansReduction: elevesAnnee.filter(eleve => !eleveHasReduction(eleve)).length,
    gratuits: elevesAnnee.filter(eleve => eleve.gratuit_mensuel).length,
    // Le chiffre de la bascule « Sans photo » : toujours compté sur toute
    // l'année, quelle que soit la recherche en cours.
    sansPhoto: elevesAnnee.filter(eleve => !aUnePhoto(eleve)).length,
    // Compteurs d effectif, affiches a la place des compteurs financiers quand
    // l ecole n a ni familles nombreuses ni comptabilite. Le sexe est stocke
    // en toutes lettres ou par initiale selon les saisies : on teste la
    // premiere lettre plutot qu une egalite stricte.
    filles: elevesAnnee.filter(e => String(e.sexe || '').trim().toUpperCase().startsWith('F')).length,
    garcons: elevesAnnee.filter(e => String(e.sexe || '').trim().toUpperCase().startsWith('M')).length,
    nombreClasses: new Set(
      elevesAnnee.map(e => e.classe_id).filter(Boolean)
    ).size,
    // Provenance de l'effectif : combien d'élèves l'école a gardés de l'année
    // précédente, combien elle en a pris de nouveaux. Lu sur type_inscription,
    // posé à l'écriture : 'renewal' par la réinscription et le passage d'année,
    // 'new' par l'inscription, 'transfert' par l'arrivée depuis une école du
    // groupe. Le transfert est compté à part : ni gardé ni recruté, il ne
    // gonfle aucun des deux chiffres.
    reinscrits: elevesAnnee.filter(e => typeInscriptionEleve(e) === 'reinscription').length,
    nouveaux: elevesAnnee.filter(e => typeInscriptionEleve(e) === 'nouveau').length,
    transferts: elevesAnnee.filter(e => typeInscriptionEleve(e) === 'transfert').length
  };

  // ⚠️ QUATRE COLONNES FIXES, ET NON TROIS : Élève, Parent/Tuteur, Demande en
  // cours, Actions. Ce nombre sert au colSpan de la ligne « aucun résultat » —
  // oublier de l'incrémenter en ajoutant une colonne ne casse rien de visible
  // tant que la liste n'est pas vide, et saute aux yeux le jour où elle l'est.
  const nombreColonnes = 4 + (famillesOuvertes ? 1 : 0) + (comptabiliteOuverte ? 1 : 0);

  // ⚠️ Tailwind lit le source, il ne calcule pas : une classe assemblee a
  // l execution ne serait jamais generee. D ou ces ternaires sur des chaines
  // completes, toutes visibles telles quelles dans le fichier.
  //
  // Barre de filtres : recherche + famille (220px) + reduction (240px). Sans
  // les deux filtres, la recherche doit prendre toute la largeur — sinon le
  // gabarit reserve des colonnes vides.
  const gabaritFiltres =
    famillesOuvertes && comptabiliteOuverte
      ? 'xl:grid-cols-[minmax(0,1fr)_220px_240px]'
      : famillesOuvertes
        ? 'xl:grid-cols-[minmax(0,1fr)_220px]'
        : comptabiliteOuverte
          ? 'xl:grid-cols-[minmax(0,1fr)_240px]'
          : 'xl:grid-cols-1';

  // Cartes : six avec la comptabilite (total, provenance, famille/filles,
  // avec, sans, gratuite), cinq sans (total, provenance, filles, garcons,
  // classes). Les cartes s elargissent au lieu de laisser un creneau vide en
  // bout de ligne.
  const gabaritCartes = comptabiliteOuverte ? 'xl:grid-cols-6' : 'xl:grid-cols-5';

  listStats.pourcentageReduction = listStats.total > 0
    ? Math.round((listStats.avecReduction / listStats.total) * 100)
    : 0;

  // Le filtre « Sans photo » ne trouve personne parce que l'année est complète
  // (et non parce qu'aucun élève n'est inscrit) : c'est l'état vide à annoncer.
  const tousOntUnePhoto = filtreSansPhoto && listStats.total > 0 && listStats.sansPhoto === 0;

  const pdfSectionOptions = [
    { id: '', label: 'Toutes les sections' },
    ...sectionsStables.map(section => ({ id: section.id, label: section.nom }))
  ];

  const pdfExportClasses = classes
    .filter(classe => !pdfExportSectionId || Number(classe.section_id) === Number(pdfExportSectionId))
    .sort((a, b) => {
      const sectionCompare = Number(a.section_id || 0) - Number(b.section_id || 0);
      if (sectionCompare !== 0) return sectionCompare;
      return String(a.nom || '').localeCompare(String(b.nom || ''), 'fr');
    });

  const togglePdfExportClasse = (classeId) => {
    setPdfExportClasseIds(prev =>
      prev.includes(classeId)
        ? prev.filter(id => id !== classeId)
        : [...prev, classeId]
    );
  };

  const handlePdfSectionChange = (sectionId) => {
    setPdfExportSectionId(sectionId);
    setPdfExportClasseIds([]);
  };

  const getPdfFilename = (response) => {
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    return match?.[1] || `Liste_Eleves_${new Date().toISOString().split('T')[0]}.pdf`;
  };



  // 🔥 NOUVEAU : Détecter les nouveaux élèves (inscrits dans les dernières 24h)
  const isNewEleve = (eleve) => {
    if (!eleve.date_inscription && !eleve.created_at) return false;

    const dateInscription = new Date(eleve.date_inscription || eleve.created_at);
    const maintenant = new Date();
    const differenceEnHeures = (maintenant - dateInscription) / (1000 * 60 * 60);

    // Considérer comme "nouveau" si inscrit dans les dernières 24h
    return differenceEnHeures <= 24;
  };

  // 🔥 NOUVEAU : Compter les nouveaux élèves par section

  const countNewElevesBySection = (sectionId) => {
    const sectionData = elevesOrganises[sectionId];
    if (!sectionData) return 0;

    let count = 0;
    Object.values(sectionData.classes).forEach(({ classe, eleves }) => {
      // Ne compter que si la classe n'a pas été vue
      if (!viewedClasses.has(classe.id)) {
        count += eleves.filter(isNewEleve).length;
      }
    });

    return count;
  };

  // 🔥 NOUVEAU : Compter les nouveaux élèves par classe

  const countNewElevesByClasse = (classeId, eleves) => {
    if (viewedClasses.has(classeId)) return 0;
    return eleves.filter(isNewEleve).length;
  };


  // 🔥 NOUVEAU : État pour tracker les classes déjà vues
  const [viewedClasses, setViewedClasses] = useState(() => {
    const saved = localStorage.getItem('viewedClasses');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // 🔥 NOUVEAU : Marquer une classe comme vue
  const markClasseAsViewed = (classeId) => {
    const newViewed = new Set(viewedClasses);
    newViewed.add(classeId);
    setViewedClasses(newViewed);
    localStorage.setItem('viewedClasses', JSON.stringify([...newViewed]));
  };

  // 🔥 NOUVEAU : Vérifier si une classe a des nouveaux non vus
  const hasUnviewedNewEleves = (classeId, eleves) => {
    if (viewedClasses.has(classeId)) return false;
    return eleves.some(isNewEleve);
  };



  // ========================================================================
  // SUITE DANS LA PARTIE 3...
  // ========================================================================

  // ============================================================================
  // PARTIE 3/6 : EFFECTS ET CHARGEMENT DES DONNÉES
  // ============================================================================

  useEffect(() => {
    setTotalPages(Math.ceil(sortedEleves.length / itemsPerPage));
  }, [sortedEleves, itemsPerPage]);



  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const activeYearResponse = await api.anneesScolaires.getActive();

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(userData);

        if (activeYearResponse.success && activeYearResponse.data) {
          setActiveYear(activeYearResponse.data);

          // ✅ CORRECTION : Charger seulement eleves et classes
          const [elevesData, classesData] = await Promise.all([
            api.eleves.getAll(activeYearResponse.data.id),
            api.classes.getAll()
          ]);

          setEleves(elevesData);
          setClasses(classesData);

          // ✅ AJOUT : Définir un objet école par défaut
          setEcole({
            nom: parametres?.nom_ecole || 'École',
            adresse: parametres?.adresse || '',
            telephone: parametres?.telephone || '',
            email: parametres?.email || ''
          });
        } else {
          setError("Aucune année scolaire active n'est définie");
        }
      } catch (error) {
        console.error('Erreur détaillée:', error); // ✅ Pour voir l'erreur exacte
        setError("Erreur lors du chargement des données");
        showErrorToast("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);


  useEffect(() => {
    loadEleves();
  }, []);

  // Le « sur combien » des réinscrits : l'effectif de l'année précédente.
  // Lu à part de la liste : un échec ne doit pas empêcher la page de
  // s'afficher, la carte montre alors les réinscrits sans dénominateur.
  // L'appel passe par Promise.resolve().then : si la méthode manquait au
  // client d'API (fichiers livrés séparément), l'erreur reste dans le catch
  // au lieu de faire tomber la page.
  useEffect(() => {
    if (!activeYear?.id) return undefined;
    let annule = false;
    Promise.resolve()
      .then(() => api.eleves.getEffectifAnneePrecedente(activeYear.id))
      .then((reponse) => {
        if (!annule) setAnneePrecedente(reponse?.success ? reponse.data : null);
      })
      .catch(() => {
        if (!annule) setAnneePrecedente(null);
      });
    return () => { annule = true; };
  }, [activeYear?.id]);

  // ========== LIEN DIRECT ?photo=sans (panneau des alertes) ==========
  // Même motif que GestionPaiementsEleves : le lien active le filtre, vide la
  // recherche globale, puis quitte l'adresse sans entrée d'historique (un F5
  // rend la liste complète). On relit l'adresse du moment plutôt que celle du
  // dernier rendu, pour ne retirer que ce paramètre et garder les autres.
  // L'effet suit la valeur lue dans l'adresse, et non le seul montage : le
  // panneau fait navigate() dans la même application, page parfois déjà ouverte.
  const lienPhoto = searchParams.get(PARAM_PHOTO);
  useEffect(() => {
    if (lienPhoto === null) return;
    if (lienPhoto === VALEUR_SANS_PHOTO) {
      setFiltrePhoto(VALEUR_SANS_PHOTO);
      setGlobalSearchTerm('');
    }
    const actuels = new URLSearchParams(window.location.search);
    if (!actuels.has(PARAM_PHOTO)) return;
    actuels.delete(PARAM_PHOTO);
    setSearchParams(actuels, { replace: true });
  }, [lienPhoto, setSearchParams]);

  const loadEleves = async () => {
    try {
      setLoading(true);
      if (activeYear) {
        const data = await api.eleves.getAll(activeYear.id);

        const elevesWithFormattedDates = data.map(eleve => {
          if (eleve.dateNaissance || eleve.date_naissance) {
            const dateStr = eleve.dateNaissance || eleve.date_naissance;

            if (typeof dateStr === 'string') {
              if (dateStr.includes('T')) {
                eleve.dateNaissance = format(parseISO(dateStr), 'yyyy-MM-dd');
              } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                eleve.dateNaissance = dateStr;
              }
            }
          }
          return eleve;
        });

        console.log("Données élèves avec dates formatées:", elevesWithFormattedDates);
        setEleves(elevesWithFormattedDates);

        // ⚠️ LES DEMANDES DE RADIATION EN COURS, LUES EN MÊME TEMPS QUE LA LISTE.
        //
        // Quand la validation passe par le Centre de Support, demander une
        // radiation ne change RIEN ici : l'élève reste affiché, parce qu'il n'est
        // pas encore radié. Le message de confirmation s'efface, et plus rien ne
        // rappelle qu'une demande court. Le 05/10/2026, un élève a porté QUATRE
        // demandes identiques, créées à dix secondes d'intervalle par quelqu'un
        // qui croyait que son clic n'avait pas abouti.
        //
        // Un appel séparé, et non une colonne de plus sur la liste des élèves :
        // la requête des élèves est déjà lourde, et cette information n'intéresse
        // que ceux qui ont le droit de radier — la route porte la même permission.
        //
        // ⚠️ Un échec ici ne doit PAS empêcher la liste de s'afficher : on perd le
        // repère, pas les élèves.
        try {
          const attente = await api.eleves.getDemandesEnAttente();
          // La route rend les deux types ; cet écran ne regarde que les radiations.
          setRadiationsEnAttente(attente?.data?.radiation || {});
        } catch (erreurAttente) {
          console.warn('Demandes de radiation en attente non chargées :', erreurAttente?.message);
          setRadiationsEnAttente({});
        }
      }
    } catch (error) {
      setError("Erreur lors du chargement des élèves");
      showErrorToast("Erreur lors du chargement des élèves");
    } finally {
      setLoading(false);
    }
  };

  // ⚠️ UN TROISIÈME ARGUMENT `options`, SANS QUOI RIEN N'EST POSSIBLE.
  // Ces aiguilleurs ne prenaient que le message : il n'existait aucun chemin
  // pour demander le toast long, même en le voulant. Les options voyagent
  // maintenant jusqu'à `shouldUseLongToast`, qui les lit en 3e argument.
  // `{ long: true, title: … }` est donc désormais honoré par cet écran.
  const setToastInfo = ({ message, type, ...options }) => {
    setToastMessage(message);
    setToastType(type);
    setToastOptions(options);
    setShowToast(true);
  };

  const showSuccessToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('success');
    setToastOptions(options);
    setShowToast(true);
  };

  const showLoadingToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('loading');
    setToastOptions(options);
    setShowToast(true);
  };

  const showErrorToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('error');
    setToastOptions(options);
    setShowToast(true);
  };







  const handleEditStart = () => {
    setIsEditing(true);
    setEditedEleve(selectedEleve);
  };

  // ========================================================================
  // SUITE DANS LA PARTIE 4...
  // ========================================================================


  // ============================================================================
  // PARTIE 4/6 : FONCTIONS EXPORT ET ÉDITION
  // ============================================================================

  const handleExportExcel = () => {
    try {
      // Les colonnes financières ne sont pas mises à zéro mais retirées : un
      // export annonçant « Montant réduction mensuelle : 0 » pour toute l'école
      // se lit comme une information, alors que c'est une absence de module.
      const dataToExport = sortedEleves.map(eleve => ({
        'Année Scolaire': activeYear.nom,
        'Nom': eleve.nom,
        'Prénom': eleve.prenom,
        'Classe': eleve.classe_nom,
        ...(famillesOuvertes && {
          'Famille': eleveHasFamily(eleve) ? eleve.famille_nom : 'Sans famille'
        }),
        ...(comptabiliteOuverte && {
          'Réduction': getReductionLabel(eleve),
          'Montant réduction mensuelle': eleve.reduction_mensuel_totale || 0,
          'Montant mensuel après réduction': eleve.frais_mensuel_avec_reduction ?? '',
          'Statut Paiement': eleve.statut_paiement
        }),
        'Parent/Tuteur': eleve.parent?.nom_prenom,
        'Email Parent': eleve.parent?.email,
        'Téléphone Parent': eleve.parent?.telephone,
        'Adresse': eleve.parent?.adresse
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Liste des élèves");

      XLSX.writeFile(wb, `Liste_des_eleves_${new Date().toLocaleDateString()}.xlsx`);
      showSuccessToast('Export Excel réussi');
    } catch (error) {
      showErrorToast("Erreur lors de l'export Excel");
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!activeYear?.id) {
        showErrorToast("Aucune année scolaire active n'est définie");
        return;
      }

      setIsExportingPdf(true);
      setToastMessage('Génération du PDF en cours...');
      setToastType('loading');
      setShowToast(true);

      const response = await api.eleves.exportPDF({
        annee_scolaire_id: activeYear.id,
        section_id: pdfExportSectionId || null,
        classe_ids: pdfExportClasseIds
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Erreur HTTP ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Le fichier PDF généré est vide');
      }

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPdfFilename(response);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowPdfExportModal(false);
      showSuccessToast('Export PDF réussi');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      showErrorToast("Erreur lors de l'export PDF");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleEditSubmit = async (updatedEleve) => {
    try {
      console.log('Données à mettre à jour:', updatedEleve);

      const dateNaissance = updatedEleve.dateNaissance;

      const response = await api.eleves.update(updatedEleve.id, {
        nom: updatedEleve.nom,
        prenom: updatedEleve.prenom,
        dateNaissance: dateNaissance,
        lieuNaissance: updatedEleve.lieuNaissance,
        nationalite: updatedEleve.nationalite,
        sexe: updatedEleve.sexe,
        photo_url: updatedEleve.photo_url,
        parent: {
          nom_prenom: updatedEleve.parent.nom_prenom,
          email: updatedEleve.parent.email,
          telephone: updatedEleve.parent.telephone,
          adresse: updatedEleve.parent.adresse
        }
      });

      setEleves(prev => prev.map(eleve =>
        eleve.id === updatedEleve.id ? { ...response, photo_url: response.photo_url || updatedEleve.photo_url } : eleve
      ));

      setSelectedEleve({ ...response, photo_url: response.photo_url || updatedEleve.photo_url });
      setIsEditing(false);
      showSuccessToast(' Les modifications ont été enregistrées avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      showErrorToast("Erreur lors de la mise à jour de l'élève");
    }
  };

  // Rappel de la fiche après l'envoi ou la suppression d'une photo. Le filtre
  // « Sans photo » se calcule sur `eleves` à chaque rendu : l'élève qui reçoit
  // sa photo quitte la liste filtrée dès cette mise à jour (et y revient si on
  // la supprime), sans rechargement ni état à resynchroniser.
  const handlePhotoChange = (updatedEleve) => {
    setEleves(prev => prev.map(eleve =>
      eleve.id === updatedEleve.id ? { ...eleve, photo_url: updatedEleve.photo_url } : eleve
    ));

    setSelectedEleve(prev =>
      prev?.id === updatedEleve.id ? { ...prev, photo_url: updatedEleve.photo_url } : prev
    );
  };

  const handleConfirmEdit = async () => {
    try {
      const updateData = {
        nom: editedEleve.nom,
        prenom: editedEleve.prenom,
        dateNaissance: editedEleve.dateNaissance,
        lieuNaissance: editedEleve.lieuNaissance,
        nationalite: editedEleve.nationalite,
        parent: {
          nom_prenom: editedEleve.parent.nom_prenom,
          email: editedEleve.parent.email,
          telephone: editedEleve.parent.telephone,
          adresse: editedEleve.parent.adresse
        }
      };

      console.log('Données envoyées pour mise à jour:', updateData);

      const response = await api.eleves.update(editedEleve.id, updateData);

      const updatedEleves = eleves.map(eleve =>
        eleve.id === editedEleve.id ? response : eleve
      );

      setEleves(updatedEleves);
      setSelectedEleve(response);
      setIsEditing(false);
      setShowConfirmation(false);
      showSuccessToast('✨ Les modifications ont été enregistrées avec succès');

    } catch (error) {
      console.error('Erreur détaillée:', error);
      showErrorToast("Erreur lors de la mise à jour de l'élève");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedEleve(null);
    setShowConfirmation(false);
    showSuccessToast('Modifications annulées');
  };

  // ========================================================================
  // SUITE DANS LA PARTIE 5...
  // ========================================================================



  // ============================================================================
  // PARTIE 5/6 : COMPOSANTS DE RENDU (SECTIONS ET CLASSES)
  // ============================================================================

  // Composant ConfirmationModal
  const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => (
    <Dialog.Root open={isOpen} onOpenChange={onCancel}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg w-full max-w-md">
          <Dialog.Title className="text-xl font-bold mb-4">
            Confirmation de modification
          </Dialog.Title>
          <p>Voulez-vous confirmer cette modification ?</p>
          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel}>Non</Button>
            <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700 text-white">
              Oui
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  // 🔥 NOUVEAU : Composant SectionCard
  // `repartition` remplace le chiffre unique par deux chiffres côte à côte,
  // chacun avec son libellé dessous. Cette carte est un peu plus haute que
  // les autres ; la grille étire toutes les cartes à la même hauteur, et le
  // dégradé suit (flex-1) au lieu de laisser une bande blanche dessous. Le
  // chiffre se centre dans la place restante.
  const StatCard = ({ icon: Icon, label, value, detail, repartition, color = 'from-[#246990] to-[#3084b5]' }) => {
    return (
      <Card className="flex flex-col border-0 shadow-md overflow-hidden">
        <div className={`flex flex-1 flex-col bg-gradient-to-r ${color} text-white p-4`}>
          <CardTitle className="text-sm font-medium flex items-center gap-1 mb-2">
            <Icon size={16} className="text-white" />
            <span className="text-xs leading-tight">{label}</span>
          </CardTitle>
          <CardDescription className="text-white/80 text-xs hidden md:block">
            {detail || 'Stats actuelles'}
          </CardDescription>
          {repartition ? (
            <div className="mt-2 grid flex-1 grid-cols-2 items-center divide-x divide-white/25">
              {repartition.map(part => (
                <div key={part.libelle} className="flex flex-col items-center justify-center px-1">
                  <span className="text-2xl font-bold leading-tight">{part.valeur}</span>
                  <span className="text-[11px] text-white/80 leading-tight">{part.libelle}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center mt-2">
              <span className="text-2xl font-bold">{value}</span>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const FilterDropdown = ({ id, value, options, onChange, placeholder, className = '' }) => {
    const isOpen = openFilterDropdown === id;
    const searchValue = filterSearchTerms[id] || '';
    const selectedOption = options.find(option => String(option.id) === String(value || ''));
    const visibleOptions = options.filter(option =>
      normalizeText(option.label).includes(normalizeText(searchValue))
    );

    return (
      <div
        className={`relative ${className}`}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setOpenFilterDropdown(isOpen ? null : id);
            setFilterSearchTerms(prev => ({ ...prev, [id]: '' }));
          }}
          className={`flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 text-sm font-medium transition-colors ${value ? 'border-[#246990]/30 text-[#246990]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
        >
          <span className="truncate">{selectedOption?.label || placeholder}</span>
          <ChevronDown size={16} className={`ml-2 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 z-30 mt-2 w-full min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="border-b border-gray-100 bg-gray-50 p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(event) => setFilterSearchTerms(prev => ({
                    ...prev,
                    [id]: event.target.value
                  }))}
                  placeholder="Rechercher..."
                  className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  autoFocus
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                />
              </div>
            </div>

            <div className="max-h-56 overflow-y-auto py-1">
              {visibleOptions.length === 0 ? (
                <div className="px-3 py-3 text-center text-sm text-gray-500">Aucun résultat</div>
              ) : (
                visibleOptions.map(option => {
                  const isSelected = String(option.id) === String(value || '');

                  return (
                    <button
                      key={`${id}-${option.id || 'all'}`}
                      type="button"
                      onClick={() => {
                        onChange(option.id);
                        setOpenFilterDropdown(null);
                        setFilterSearchTerms(prev => ({ ...prev, [id]: '' }));
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#246990]/10 font-semibold text-[#246990]' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <span>{option.label}</span>
                      {isSelected && <span className="h-2 w-2 rounded-full bg-[#246990]" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSectionCard = ({ section, sectionData }) => {
    const colors = getSectionColors(section.id);
    const stats = calculateSectionStats(section.id);
    // Le filtre « Sans photo » ouvre les sections, comme la recherche globale,
    // mais pas les classes : l'agent choisit sa classe plutôt que de recevoir
    // tous les manques de l'école d'un coup.
    const ouvertureImposee = hasGlobalSearch || filtreSansPhoto;
    const isExpanded = ouvertureImposee || expandedSections.has(section.id);
    const classesArray = Object.values(sectionData.classes);
    const sectionFilter = sectionFilters[section.id] || {};
    const sectionSearch = sectionSearchTerms[section.id] || '';
    const sectionScopedClasses = classesArray.map(({ classe, eleves }) => ({
      classe,
      eleves: applyLocalEleveFilters(eleves, {
        search: hasGlobalSearch ? '' : sectionSearch,
        famille: hasGlobalSearch ? '' : sectionFilter.famille || '',
        reduction: hasGlobalSearch ? '' : sectionFilter.reduction || ''
      })
    })).filter(({ eleves }) => eleves.length > 0);
    const visibleElevesCount = sectionScopedClasses.reduce((sum, classeItem) => sum + classeItem.eleves.length, 0);

    // Si aucune classe dans cette section, ne pas afficher
    if (classesArray.length === 0) return null;

    return (
      <div className="mb-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Header de section */}
        <div
          className="cursor-pointer p-5 transition-colors"
          style={{ backgroundColor: colors.light }}
          onClick={() => {
            if (ouvertureImposee) return;
            const newExpanded = new Set(expandedSections);
            if (isExpanded) newExpanded.delete(section.id);
            else newExpanded.add(section.id);
            setExpandedSections(newExpanded);
          }}
        >


          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[minmax(0,1fr)_96px_96px_74px_32px]">
            <div className="min-w-0">
              <h3 className="truncate text-xl font-bold" style={{ color: colors.primary }}>
                {section.nom}
              </h3>
              <p className="mt-1 text-sm text-gray-600">Classes et élèves de la section</p>
            </div>

            <div className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-[#246990]/20 bg-[#246990]/10 px-2 text-xs font-semibold text-[#246990]">
              <span>{stats.totalEleves}</span>
              <span className="font-medium">élèves</span>
            </div>

            <div className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-[#319190]/20 bg-[#319190]/10 px-2 text-xs font-semibold text-[#319190]">
              <span>{stats.totalClasses}</span>
              <span className="font-medium">classes</span>
            </div>

            <div className="flex md:justify-end">
              {(() => {
                const newCount = countNewElevesBySection(section.id);
                if (newCount > 0) {
                  return (
                    <span className="inline-flex items-center rounded-md border border-[#c66d59]/20 bg-[#c66d59]/10 px-2 py-0.5 text-xs font-semibold text-[#c66d59]">
                      {newCount} nouveau{newCount > 1 ? 'x' : ''}
                    </span>
                  );
                }

              })()}
            </div>

            <ChevronDown
              className="justify-self-end transition-transform"
              size={22}
              style={{
                color: colors.primary,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
        </div>



        {/* Contenu de la section (classes) */}
        {isExpanded && (
          <div className="border-t" style={{ borderColor: colors.secondary + '20' }}>
            <div
              className="border-b border-gray-100 bg-white p-4"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={`grid grid-cols-1 gap-3 ${gabaritFiltres}`}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={sectionSearch}
                    onChange={(event) => setSectionSearchTerms(prev => ({
                      ...prev,
                      [section.id]: event.target.value
                    }))}
                    placeholder={`Rechercher dans ${section.nom}...`}
                    className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  />
                </div>

                {famillesOuvertes && (
                <FilterDropdown
                  id={`section-${section.id}-famille`}
                  value={sectionFilter.famille || ''}
                  options={familleFilterOptions}
                  onChange={(value) => updateSectionFilter(section.id, 'famille', value)}
                  placeholder="Filtrer les familles"
                />
                )}

                {comptabiliteOuverte && (
                <FilterDropdown
                  id={`section-${section.id}-reduction`}
                  value={sectionFilter.reduction || ''}
                  options={reductionFilterOptions}
                  onChange={(value) => updateSectionFilter(section.id, 'reduction', value)}
                  placeholder="Filtrer les réductions"
                />
                )}
              </div>

              <p className="mt-3 text-xs font-medium text-gray-500">
                {visibleElevesCount} élève(s) affiché(s) dans cette section
              </p>
            </div>
            {sectionScopedClasses.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Aucun élève</h4>
                <p className="text-gray-600">
                  Aucun élève trouvé dans cette section
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {sectionScopedClasses.map(({ classe, eleves }) => (
                  <React.Fragment key={classe.id}>
                    {renderClasseCard({ classe, eleves, colors })}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 🔥 NOUVEAU : Composant ClasseCard
  const renderClasseCard = ({ classe, eleves, colors }) => {
    const isExpanded = hasGlobalSearch || expandedClasses.has(classe.id);
    const classFilter = classFilters[classe.id] || {};
    const localSearchTerm = classSearchTerms[classe.id] || '';
    const elevesVisibles = applyLocalEleveFilters(eleves, {
      search: hasGlobalSearch ? '' : localSearchTerm,
      famille: hasGlobalSearch ? '' : classFilter.famille || '',
      reduction: hasGlobalSearch ? '' : classFilter.reduction || ''
    });

    return (
      <div className="p-4">
        {/* Header de classe */}

        <div
          className="grid cursor-pointer grid-cols-1 items-center gap-4 rounded-lg bg-gray-50 px-1 py-4 transition-colors hover:bg-gray-100 md:grid-cols-[minmax(0,1fr)_96px_96px_74px_32px]"
          onClick={() => {
            if (hasGlobalSearch) return;
            const newExpanded = new Set(expandedClasses);
            if (isExpanded) {
              newExpanded.delete(classe.id);
            } else {
              newExpanded.add(classe.id);
              markClasseAsViewed(classe.id);
            }
            setExpandedClasses(newExpanded);
          }}
        >
          <div className="min-w-0">
            <h4 className="truncate text-base font-bold text-gray-950">{classe.nom}</h4>

          </div>

          <div className="hidden md:block" aria-hidden="true" />

          <div className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md border border-[#246990]/20 bg-[#246990]/10 px-2 text-xs font-semibold text-[#246990]">
            <span>{eleves.length}</span>
            <span className="font-medium">élèves</span>
          </div>

          <div className="flex md:justify-end">
            {(() => {
              const newCount = countNewElevesByClasse(classe.id, eleves);
              if (newCount > 0) {
                return (
                  <span className="inline-flex items-center rounded-md border border-[#c66d59]/20 bg-[#c66d59]/10 px-2 py-0.5 text-xs font-semibold text-[#c66d59]">
                    {newCount} nouveau{newCount > 1 ? 'x' : ''}
                  </span>
                );
              }

            })()}
          </div>

          <ChevronDown
            className="justify-self-end transition-transform"
            size={20}
            style={{
              color: colors.primary,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </div>

        {/* Liste des élèves */}
        {isExpanded && (
          <div className="mt-4">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div
                className="border-b border-gray-100 bg-white p-4"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className={`grid grid-cols-1 gap-3 ${gabaritFiltres}`}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={localSearchTerm}
                      onChange={(e) => setClassSearchTerms(prev => ({
                        ...prev,
                        [classe.id]: e.target.value
                      }))}
                      placeholder={`Rechercher dans ${classe.nom}...`}
                      className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                    />
                  </div>

                  {famillesOuvertes && (
                  <FilterDropdown
                    id={`classe-${classe.id}-famille`}
                    value={classFilter.famille || ''}
                    options={familleFilterOptions}
                    onChange={(value) => updateClassFilter(classe.id, 'famille', value)}
                    placeholder="Filtrer les familles"
                  />
                  )}

                  {comptabiliteOuverte && (
                  <FilterDropdown
                    id={`classe-${classe.id}-reduction`}
                    value={classFilter.reduction || ''}
                    options={reductionFilterOptions}
                    onChange={(value) => updateClassFilter(classe.id, 'reduction', value)}
                    placeholder="Filtrer les réductions"
                  />
                  )}
                </div>
                <p className="mt-3 text-xs font-medium text-gray-500">
                  {elevesVisibles.length} élève(s) affiché(s) dans cette classe
                </p>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs text-gray-500 font-medium uppercase">Élève</th>
                    {famillesOuvertes && (
                      <th className="py-3 px-4 text-left text-xs text-gray-500 font-medium uppercase">Famille</th>
                    )}
                    {comptabiliteOuverte && (
                      <th className="py-3 px-4 text-left text-xs text-gray-500 font-medium uppercase">Réduction</th>
                    )}
                    <th className="py-3 px-4 text-left text-xs text-gray-500 font-medium uppercase">Parent/Tuteur</th>
                    {/*
                      Une COLONNE pour la demande en cours, et non un badge collé au
                      nom. Placée juste avant « Actions » : c'est là que l'œil va au
                      moment de décider s'il radie, et la colonne du nom reste lisible.
                    */}
                    <th className="py-3 px-4 text-left text-xs text-gray-500 font-medium uppercase">Demande en cours</th>
                    <th className="py-3 px-4 text-center text-xs text-gray-500 font-medium uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {elevesVisibles.map(eleve => (
                    <tr key={eleve.id} className="hover:bg-gray-50 transition-colors">

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {eleve.nom} {eleve.prenom}
                          {/* Le badge disparaît si la classe a déjà été consultée. */}
                          {isNewEleve(eleve) && !viewedClasses.has(classe.id) && (
                            <span className="inline-flex items-center rounded-md border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                              Nouveau
                            </span>
                          )}
                        </div>
                      </td>

                      {famillesOuvertes && (
                      <td className="py-3 px-4">
                        {eleveHasFamily(eleve) ? (
                          <div className="inline-flex max-w-[160px] items-center gap-1.5 rounded-md border border-[#246990]/20 bg-[#246990]/10 px-2 py-0.5 text-xs font-semibold text-[#246990]">
                            <Users size={12} />
                            <span className="truncate">{eleve.famille_nom}</span>
                          </div>
                        ) : (
                          <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-500">
                            Sans famille
                          </span>
                        )}
                      </td>
                      )}

                      {comptabiliteOuverte && (
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${getReductionBadgeClass(eleve)}`}>
                            {getReductionLabel(eleve)}
                          </span>
                          <p className="text-xs text-gray-500">{getReductionAmountLabel(eleve)}</p>
                        </div>
                      </td>
                      )}



                      <td className="py-3 px-4">{eleve.parent?.nom_prenom}</td>
                      {/*
                        LA COLONNE DE L'ATTENTE. Le badge était collé au nom, dans la
                        même cellule : il bousculait la ligne et se lisait mal. Il a
                        sa colonne, juste avant les actions, là où l'œil va avant de
                        cliquer.
                      */}
                      <td className="py-3 px-4">
                        {radiationsEnAttente[eleve.id] ? (
                          <span className="inline-flex items-center whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            Radiation demandée
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-2">
                          <ProtectedComponent module="ELEVES" sousModule="DETAILS" action="VIEW">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded-md border border-[#246990]/20 bg-[#246990]/10 px-2 py-0.5 text-xs font-semibold text-[#246990] transition-colors hover:bg-[#246990]/15"
                              onClick={() => {
                                setSelectedEleve(eleve);
                                setIsDetailsOpen(true);
                              }}
                            >
                              <Folder size={13} />
                              Dossier
                            </button>
                          </ProtectedComponent>

                          <ProtectedComponent module="ELEVES" sousModule="RADIATION" action="CREATE">
                            {/*
                              GRISÉ QUAND UNE DEMANDE COURT. Le bouton restait offert :
                              on recliquait, le serveur refusait, et rien ne le disait.
                              Le refus existe toujours côté serveur — c'est lui qui fait
                              foi — mais l'écran ne propose plus le geste inutile.
                            */}
                            <button
                              type="button"
                              disabled={Boolean(radiationsEnAttente[eleve.id])}
                              onClick={() => {
                                setEleveToRadiate(eleve);
                                setShowConfirmRadiation(true);
                              }}
                              className="inline-flex items-center gap-1.5 rounded-md border border-[#c66d59]/20 bg-[#c66d59]/10 px-2 py-0.5 text-xs font-semibold text-[#c66d59] transition-colors hover:bg-[#c66d59]/15 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#c66d59]/10"
                            >
                              <Trash2 size={13} />
                              {radiationsEnAttente[eleve.id] ? 'En attente' : 'Radiation'}
                            </button>
                          </ProtectedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {elevesVisibles.length === 0 && (
                    <tr>
                      <td colSpan={nombreColonnes} className="py-8 text-center text-sm text-gray-500">
                        Aucun élève ne correspond à cette recherche dans la classe.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========================================================================
  // SUITE DANS LA PARTIE 6 (RENDU PRINCIPAL)...
  // ========================================================================



  // ============================================================================
  // PARTIE 6/6 : RENDU PRINCIPAL
  // ============================================================================

  // Rendu principal
  return (
    <div className="w-full p-8 bg-gradient-to-br from-white-50 to-gray-100" onClick={() => setOpenFilterDropdown(null)}>
      {/*
        Le chargement reprend la forme employée partout ailleurs : l'icône
        Loader2 qui tourne, en couleur de marque, avec son libellé. Cet écran
        n'affichait qu'un texte nu, et son voisin — la liste des radiés — faisait
        tourner l'icône du geste « rafraîchir ». Deux écrans du même module ne se
        ressemblaient pas.
      */}
      {loading && (
        <div className="py-8 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#246990]" />
          <p className="mt-3 text-sm text-gray-600">Chargement des élèves…</p>
        </div>
      )}
      {error && <div className="text-red-500 text-center py-4">{error}</div>}

      {/* 🔥 NOUVEAU : Affichage par sections */}
      <ProtectedComponent
        module="ELEVES"
        sousModule="LISTE"
        action="VIEW"
        fallback={<div className="p-8 text-center">Vous n'avez pas l'autorisation de voir la liste des élèves.</div>}
      >
        <div className="space-y-6">
          {/* En-tête avec titre, actions et statistiques */}
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#246990]">Liste des élèves</h1>

            </div>

            <div className="flex flex-wrap gap-3 xl:justify-end">
              <ProtectedComponent module="ELEVES" sousModule="LISTE" action="EXPORT_EXCEL">
                <button
                  onClick={handleExportExcel}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#246990] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#246990]/90"
                >
                  <Download size={17} />
                  Export Excel
                </button>
              </ProtectedComponent>

              <ProtectedComponent module="ELEVES" sousModule="LISTE" action="EXPORT_PDF">
                <button
                  onClick={() => setShowPdfExportModal(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#319190] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#319190]/90"
                >
                  <FileText size={17} />
                  Export PDF
                </button>
              </ProtectedComponent>

              <ProtectedComponent module="ELEVES" sousModule="RADIATION" action="VIEW_LIST">
                <button
                  onClick={() => navigate('/administration/dossier-eleves/radies')}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-[#c66d59]/25 bg-[#c66d59]/10 px-4 text-sm font-semibold text-[#a94f3d] transition-colors hover:bg-[#c66d59]/15"
                >
                  <Trash2 size={17} />
                  Élèves radiés
                </button>
              </ProtectedComponent>
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${gabaritCartes}`}>
            <StatCard
              icon={Users}
              label="Total élèves"
              value={listStats.total}
              detail="Année active"
              color="from-[#246990] to-[#3084b5]"
            />
            {/* Provenance de l effectif : gardes de l annee precedente contre
                recrutes cette annee. Les arrivees par transfert ne sont ni
                l un ni l autre : on les nomme dans le detail des qu il y en a,
                pour que les deux chiffres et le total se reconcilient.
                Les reinscrits se lisent « sur » l effectif de l annee
                precedente (eleves restes actifs jusqu au bout) : c est le
                taux de reinscription. Premiere annee de l ecole ou lecture
                en echec : le chiffre seul, et la part de nouveaux. */}
            <StatCard
              icon={UserPlus}
              label="Réinscrits / Nouveaux"
              repartition={[
                {
                  libelle: 'Réinscrits',
                  valeur: anneePrecedente ? (
                    <>
                      {listStats.reinscrits}
                      <span className="text-sm font-semibold text-white/75"> / {anneePrecedente.effectif}</span>
                    </>
                  ) : listStats.reinscrits
                },
                { libelle: 'Nouveaux', valeur: listStats.nouveaux }
              ]}
              detail={[
                anneePrecedente && anneePrecedente.effectif > 0
                  ? `${Math.round((listStats.reinscrits / anneePrecedente.effectif) * 100)}% des élèves de ${anneePrecedente.annee_nom}`
                  : `${listStats.total ? Math.round((listStats.nouveaux / listStats.total) * 100) : 0}% de nouveaux`,
                // Réinscrits partis depuis : Admissions les compte encore
                // (« dont N partis depuis »), cette carte non. Les nommer ici
                // fait se recouper les deux écrans.
                anneePrecedente?.reinscrits_partis > 0
                  ? `${anneePrecedente.reinscrits_partis} réinscrit${anneePrecedente.reinscrits_partis > 1 ? 's' : ''} parti${anneePrecedente.reinscrits_partis > 1 ? 's' : ''} depuis`
                  : null,
                listStats.transferts > 0
                  ? `${listStats.transferts} transfert${listStats.transferts > 1 ? 's' : ''}`
                  : null
              ].filter(Boolean).join(' · ')}
              color="from-[#1f5a7a] to-[#319190]"
            />
            {/* Les compteurs financiers cedent la place a des compteurs
                d effectif quand l ecole n a ni familles nombreuses ni
                comptabilite. On ne laisse pas une carte « Total » seule : ce
                serait lu comme un ecran casse, pas comme un choix. */}
            {famillesOuvertes && (
            <StatCard
              icon={UserCheck}
              label="En famille"
              value={listStats.enFamille}
              detail={`${listStats.total ? Math.round((listStats.enFamille / listStats.total) * 100) : 0}% des élèves`}
              color="from-[#c66d59] to-[#ae7467]"
            />
            )}
            {comptabiliteOuverte && (
            <StatCard
              icon={Percent}
              label="Avec réduction"
              value={listStats.avecReduction}
              detail={`${listStats.pourcentageReduction}% des élèves`}
              color="from-[#8B5CF6] to-[#A855F7]"
            />
            )}
            {comptabiliteOuverte && (
            <StatCard
              icon={UserX}
              label="Sans réduction"
              value={listStats.sansReduction}
              detail="Tarif normal"
              color="from-[#319190] to-[#63b5b4]"
            />
            )}

            {!famillesOuvertes && (
            <StatCard
              icon={UserCheck}
              label="Filles"
              value={listStats.filles}
              detail={`${listStats.total ? Math.round((listStats.filles / listStats.total) * 100) : 0}% de l'effectif`}
              color="from-[#c66d59] to-[#ae7467]"
            />
            )}
            {!comptabiliteOuverte && (
            <StatCard
              icon={Users}
              label="Garçons"
              value={listStats.garcons}
              detail={`${listStats.total ? Math.round((listStats.garcons / listStats.total) * 100) : 0}% de l'effectif`}
              color="from-[#8B5CF6] to-[#A855F7]"
            />
            )}
            {!comptabiliteOuverte && (
            <StatCard
              icon={School}
              label="Classes"
              value={listStats.nombreClasses}
              detail="Classes avec au moins un élève"
              color="from-[#319190] to-[#63b5b4]"
            />
            )}

            {comptabiliteOuverte && (
            <StatCard
              icon={CreditCard}
              label="Gratuité"
              value={listStats.gratuits}
              detail="Mensuel à 0 FCFA"
              color="from-gray-800 to-gray-700"
            />
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-bold uppercase tracking-wide text-[#246990]">Recherche globale</p>
                <p className="mt-1 text-sm text-gray-500">
                  Retrouvez un élève sans connaître sa section ni sa classe. Les résultats s'ouvrent automatiquement.
                </p>
              </div>

              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center xl:max-w-2xl">
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                  <input
                    type="text"
                    value={globalSearchTerm}
                    onChange={(event) => setGlobalSearchTerm(event.target.value)}
                    placeholder="Rechercher par nom, prénom, matricule, classe, famille ou parent..."
                    className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 pl-10 pr-24 text-sm outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                  />
                  {hasGlobalSearch && (
                    <button
                      type="button"
                      onClick={() => setGlobalSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      Effacer
                    </button>
                  )}
                </div>

                {/* La bascule « Sans photo », sans icône. Grise au repos : le
                    manque de photo se dit en gris neutre, jamais en ambre ni en
                    rouge (règle actée dans parametrage/PhotosEleves.jsx) ; elle
                    ne prend la couleur de l'école que lorsqu'elle filtre. */}
                <button
                  type="button"
                  aria-pressed={filtreSansPhoto}
                  onClick={() => setFiltrePhoto(filtreSansPhoto ? '' : VALEUR_SANS_PHOTO)}
                  className={`inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border px-4 text-sm font-semibold transition-colors ${filtreSansPhoto
                    ? 'border-[#246990] bg-[#246990]/10 text-[#246990]'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Sans photo · {listStats.sansPhoto}
                </button>
              </div>
            </div>

            {filtreSansPhoto && listStats.total > 0 && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#246990]/15 bg-[#246990]/5 px-3 py-2 text-sm font-medium text-[#246990]">
                <span>
                  {listStats.sansPhoto} élève{listStats.sansPhoto > 1 ? 's' : ''} sans photo sur {listStats.total} élève{listStats.total > 1 ? 's' : ''} de l'année
                </span>
                <button
                  type="button"
                  onClick={() => setFiltrePhoto('')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Effacer
                </button>
              </div>
            )}

            {hasGlobalSearch && (
              <div className="mt-3 rounded-md border border-[#246990]/15 bg-[#246990]/5 px-3 py-2 text-sm font-medium text-[#246990]">
                {filteredEleves.length} résultat{filteredEleves.length > 1 ? 's' : ''} trouvé{filteredEleves.length > 1 ? 's' : ''} {filtreSansPhoto
                  ? 'parmi les élèves sans photo.'
                  : `sur ${elevesAnnee.length} élèves de l'année active.`}
              </div>
            )}
          </div>

          {/* Sections avec classes et élèves */}
          {sectionsStables.map(section => {
            const sectionData = elevesOrganises[section.id];
            if (!sectionData) return null;

            return (
              <React.Fragment key={section.id}>
                {renderSectionCard({ section, sectionData })}
              </React.Fragment>
            );
          })}

          {/* Message si aucun élève */}
          {sortedEleves.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {tousOntUnePhoto
                  ? 'Aucun élève sans photo'
                  : hasGlobalSearch ? 'Aucun résultat' : 'Aucun élève trouvé'}
              </h3>
              <p className="text-gray-600">
                {tousOntUnePhoto
                  ? "Tous les élèves de l'année ont une photo."
                  : hasGlobalSearch
                    ? (filtreSansPhoto
                      ? "Aucun élève sans photo ne correspond à cette recherche globale."
                      : "Aucun élève ne correspond à cette recherche globale.")
                    : "Aucun élève n'est inscrit pour le moment."}
              </p>
            </div>
          )}
        </div>
      </ProtectedComponent>

      <Dialog.Root open={showPdfExportModal} onOpenChange={setShowPdfExportModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[10000] bg-black/55" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-[10001] w-[min(720px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-gray-100 bg-[#246990] px-6 py-5 text-white">
              <Dialog.Title className="text-xl font-bold">Exporter la liste des élèves</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-white/80">
                Choisissez la portée du fichier PDF.
              </Dialog.Description>
            </div>

            <div className="space-y-5 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                <FilterDropdown
                  id="pdf-export-section"
                  value={pdfExportSectionId}
                  options={pdfSectionOptions}
                  onChange={handlePdfSectionChange}
                  placeholder="Toutes les sections"
                />

                <div className="inline-flex h-10 items-center justify-center rounded-md border border-[#246990]/20 bg-[#246990]/10 px-3 text-sm font-semibold text-[#246990]">
                  {pdfExportClasseIds.length > 0 ? `${pdfExportClasseIds.length} classe(s)` : 'Toutes classes'}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200">
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">Classes incluses</p>
                    <p className="text-xs text-gray-500">
                      {pdfExportClasses.length} classe(s) disponible(s) dans la portée sélectionnée.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPdfExportClasseIds(pdfExportClasses.map(classe => classe.id))}
                      className="rounded-md border border-[#246990]/20 bg-white px-3 py-1.5 text-xs font-semibold text-[#246990] hover:bg-[#246990]/5"
                    >
                      Tout cocher
                    </button>
                    <button
                      type="button"
                      onClick={() => setPdfExportClasseIds([])}
                      className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Tout retirer
                    </button>
                  </div>
                </div>

                <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto">
                  {pdfExportClasses.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      Aucune classe disponible pour cette sélection.
                    </div>
                  ) : (
                    pdfExportClasses.map(classe => {
                      const section = sectionsStables.find(item => Number(item.id) === Number(classe.section_id));
                      const checked = pdfExportClasseIds.includes(classe.id);

                      return (
                        <button
                          key={`pdf-classe-${classe.id}`}
                          type="button"
                          onClick={() => togglePdfExportClasse(classe.id)}
                          className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${checked ? 'bg-[#246990]/5' : 'hover:bg-gray-50'}`}
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900">{classe.nom}</p>
                            <p className="text-xs text-gray-500">{section?.nom || 'Section non renseignée'}</p>
                          </div>
                          <span className={`flex h-5 w-5 items-center justify-center rounded border ${checked ? 'border-[#246990] bg-[#246990]' : 'border-gray-300 bg-white'}`}>
                            {checked && <span className="h-2 w-2 rounded-full bg-white" />}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                className="h-10 rounded-md border-gray-200"
                onClick={() => setShowPdfExportModal(false)}
                disabled={isExportingPdf}
              >
                Annuler
              </Button>
              <Button
                type="button"
                className="h-10 rounded-md bg-[#246990] px-4 text-white hover:bg-[#246990]/90"
                onClick={handleExportPDF}
                disabled={isExportingPdf || pdfExportClasses.length === 0}
              >
                {isExportingPdf ? 'Génération...' : 'Générer le PDF'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Modaux */}
      {selectedEleve && (
        <>
          <DetailModal
            eleve={selectedEleve}
            isOpen={isDetailsOpen}
            onClose={() => {
              setIsDetailsOpen(false);
              setIsEditing(false);
              setEditedEleve(null);
            }}
            onUpdate={handleEditSubmit}
            onPhotoChange={handlePhotoChange}
            onShowToast={(message, type) => setToastInfo({ message, type })}
          />




        </>
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmEdit}
        onCancel={handleCancelEdit}
      />

      {/*
        ⚠️ L'ARBITRAGE, ET NON `<Toast>` EN DUR.
        Cet écran rendait le toast court directement : il ne pouvait donc PAS
        honorer une option `long`, le composant court n'ayant ni `options`, ni
        `title`, ni `long`. Et le seuil automatique des 130 caractères ne le
        sauvait pas — les messages de ce module mesurent entre 44 et 104
        caractères, donc tous condamnés aux 3 secondes du toast court.
        Même motif que FamillesNombreuses et EmployeesList, déjà en place.
      */}
      {showToast && (
        shouldUseLongToast(toastMessage, toastType, toastOptions) ? (
          <LongToast
            show
            title={toastOptions.title}
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        ) : (
          <Toast
            duration={toastOptions.duration}
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )
      )}

      {showConfirmRadiation && eleveToRadiate && (
        <ConfirmDialog
          isOpen={showConfirmRadiation}
          onClose={() => {
            if (isRadiating) return;
            setShowConfirmRadiation(false);
            setEleveToRadiate(null);
            setRadiationPreview(null);
            setIsLoadingRadiationPreview(false);
          }}
          onConfirm={async (reasonId) => {
            try {
              setIsRadiating(true);
              showLoadingToast(`Radiation de ${eleveToRadiate.prenom} ${eleveToRadiate.nom} en cours...`);
              const reponse = await api.eleves.radiate(eleveToRadiate.id, {
                reason_id: reasonId ? parseInt(reasonId) : null,
                date_radiation: new Date().toISOString().split("T")[0],
                annee_scolaire_id: activeYear.id,
              });

              // La radiation passe désormais par le Centre de Support : tant que la
              // demande n'est pas approuvée, l'élève reste actif. Le retirer de la
              // liste et annoncer une radiation faite serait doublement faux.
              if (reponse?.validation_required) {
                // ⚠️ LE REPÈRE SE POSE TOUT DE SUITE, SANS ATTENDRE UN RECHARGEMENT.
                // C'est lui qui remplace la radiation que l'utilisateur s'attendait
                // à voir : la ligne change sous ses yeux, donc il ne reclique pas.
                setRadiationsEnAttente((prev) => ({
                  ...prev,
                  [eleveToRadiate.id]: {
                    demande_id: reponse?.data?.demande_id || null,
                    demande_le: null,
                    demandeur: null
                  }
                }));
                showSuccessToast(
                  `Demande de radiation envoyée au Centre de Support pour ${eleveToRadiate.prenom} ${eleveToRadiate.nom}. Elle prendra effet après validation.`,
                  // ⚠️ UNE DURÉE, PAS UN BANDEAU. Avec la comptabilité active, toute
                  // radiation passe par la validation : ce message est donc le cas
                  // NORMAL, et le cas en lot. Un bandeau à fermer coûterait un clic
                  // par élève. 94 caractères de gabarit + le nom : il ne franchit le
                  // seuil des 130 que pour les noms très longs, donc le même geste se
                  // comportait différemment selon l'élève.
                  { duration: 8000 }
                );
              } else {
                setEleves((prev) => prev.filter((e) => e.id !== eleveToRadiate.id));
                showSuccessToast(
                  `L'élève ${eleveToRadiate.prenom} ${eleveToRadiate.nom} a été radié.`
                );
              }
            } catch (error) {
              // ⚠️ LE MESSAGE DU SERVEUR, QUAND IL EN DONNE UN.
              // « Erreur lors de la radiation » ne dit rien, et c'est précisément ce
              // que voit quelqu'un dont la demande est refusée parce qu'une autre
              // court déjà : le serveur nomme la demande existante, son numéro et sa
              // date, et ce message-là lui évite de recommencer une troisième fois.
              // ⚠️ BANDEAU PERSISTANT. C'est ici qu'arrive le refus de la garde des
              // doublons, qui nomme la demande déjà en attente, son numéro et sa
              // date. Rare, et précisément celui qu'il ne faut pas manquer. Pas de
              // `title` : LongToast pose « Opération refusée », et un titre ajouté
              // ne ferait que paraphraser le message.
              showErrorToast(error?.message || "Erreur lors de la radiation.", { persistent: true });
            } finally {
              setIsRadiating(false);
              setShowConfirmRadiation(false);
              setEleveToRadiate(null);
              setRadiationPreview(null);
              setIsLoadingRadiationPreview(false);
            }
          }}
          title="Confirmer la radiation"
          description={`Souhaitez-vous vraiment radier ${eleveToRadiate.prenom} ${eleveToRadiate.nom} de l'établissement ?`}
          confirmText="Oui, radier"
          cancelText="Annuler"
          type="warning"
          requireReason={true}
          isLoading={isRadiating}
          contentClassName="sm:max-w-3xl"
        >
          {renderRadiationPreview()}
        </ConfirmDialog>
      )}
    </div>
  );
};

export default StudentsList;
